import validateAccept from "attr-accept";
import filter from "lodash/filter";
import { lookup } from "mime-types";
import React, { useEffect, useRef, useState } from "react";
import { ValidateFileFunction } from "shared/files/fileUpload";
import { useLocalize } from "shared/hooks/useLocalize";
import { showErrorToast } from "uiKit/container/toast/helpers";
import { LocalizationId } from "uiKit/inputs/fileInput/localization";

export interface FileInputProps {
    onFilesChange?: (validFiles: File[]) => Promise<void> | void;
    multiple?: boolean;
    children: React.FC<{ onInputClick: () => void }>;
    accept: string;
    validate?: ValidateFileFunction;
    clearFiles?: boolean;
    disabled?: boolean;
}

const FileInput: React.FC<FileInputProps> = ({
    onFilesChange,
    multiple,
    accept,
    children,
    validate,
    clearFiles,
    disabled,
}: FileInputProps) => {
    const inputFileRef = useRef<HTMLInputElement>(null);
    const showFileDialog = () => inputFileRef.current && inputFileRef.current.click();
    const [files, setFiles] = useState("");
    const localize = useLocalize();

    useEffect(() => {
        if (clearFiles) {
            setFiles("");
        }
    }, [clearFiles]);

    const Visualization = children;

    function isFileAcceptedAndValid(file: File) {
        return (
            validateAccept({ name: file.name, type: file.type ?? (lookup(file.name) || "") }, accept) &&
            (!validate || validate(file))
        );
    }

    return (
        <div className="input-file">
            <input
                accept={accept}
                style={{ display: "none" }}
                ref={inputFileRef}
                type="file"
                multiple={multiple}
                value={files}
                disabled={disabled}
                onChange={async e => {
                    if (!e.target.files) {
                        return;
                    }

                    const selectedFiles = Array.from(e.target.files);
                    const validFiles = filter(selectedFiles, isFileAcceptedAndValid);
                    const notAcceptedFiles = filter(selectedFiles, (f: File) => !isFileAcceptedAndValid(f));

                    if (notAcceptedFiles && notAcceptedFiles.length > 0) {
                        showErrorToast({ message: localize(LocalizationId.InvalidFileFormat) });
                    }

                    await onFilesChange?.(validFiles);
                    setFiles("");
                }}
            />
            <Visualization onInputClick={() => showFileDialog()} />
        </div>
    );
};

export default FileInput;
